import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input } from 'reactstrap';
import api from '../../api';
import './PlayerShopModals.css';

const PlayerShopBrowseModal = ({ isOpen, toggle, lotId, shopName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [userCoins, setUserCoins] = useState(0);
  const [purchaseQuantities, setPurchaseQuantities] = useState({});
  
  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const fetchShopItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/player-shops/lot/${lotId}/items`, { withCredentials: true });
      setShopItems(response.data || []);
      
      // Initialize purchase quantities
      const quantities = {};
      response.data.forEach(item => {
        quantities[item.id] = 1;
      });
      setPurchaseQuantities(quantities);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching shop items:', err);
      setError('Failed to load shop items. Please try again.');
      setLoading(false);
    }
  }, [lotId]);

  const fetchUserCoins = useCallback(async () => {
    try {
      // Fix: Use the correct profile endpoint
      const response = await api.get('/v1/profile', { withCredentials: true });
      setUserCoins(response.data.coins || 0);
    } catch (err) {
      console.error('Error fetching user coins:', err);
    }
  }, []);

  useEffect(() => {
    if (isOpen && lotId) {
      fetchShopItems();
      fetchUserCoins();
    }
  }, [isOpen, lotId, fetchShopItems, fetchUserCoins]);

  const handleQuantityChange = (itemId, value) => {
    const item = shopItems.find(i => i.id === itemId);
    if (!item) return;
    
    const maxQuantity = item.quantity;
    const newQuantity = Math.max(1, Math.min(maxQuantity, parseInt(value) || 1));
    
    setPurchaseQuantities(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  };

  const handlePurchase = async (item) => {
    const quantity = purchaseQuantities[item.id] || 1;
    const totalCost = item.price * quantity;
    
    if (userCoins < totalCost) {
      setError(`You don't have enough coins. Need ${formatNumber(totalCost)}, have ${formatNumber(userCoins)}.`);
      return;
    }
    
    try {
      setLoading(true);
      await api.post('/v1/player-shops/purchase-item', {
        shop_item_id: item.id,
        quantity
      }, { withCredentials: true });
      
      // Refresh data
      fetchShopItems();
      fetchUserCoins();
      
      setSuccess(`Successfully purchased ${quantity} ${item.item_name}(s)!`);
      setLoading(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error purchasing item:', err);
      setError(err.response?.data?.detail || 'Failed to purchase item. Please try again.');
      setLoading(false);
    }
  };

  const canAfford = (item, quantity) => {
    return userCoins >= (item.price * quantity);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="player-shop-browse-modal" size="lg" contentClassName="modal-content-dark">
      <ModalHeader toggle={toggle} style={{ backgroundColor: '#1d222e', color: '#fff', borderBottom: '1px solid #2a3042' }} className="modal-colored-header">
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{shopName || 'Player Shop'}</span>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px', 
            background: 'rgba(255, 193, 7, 0.2)', 
            padding: '4px 8px', 
            borderRadius: '4px',
            whiteSpace: 'nowrap'
          }}>
            <img src="items/coin.png" alt="Coin" style={{ width: 16, height: 16, flexShrink: 0 }} />
            <span>{formatNumber(userCoins)}</span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: '#1d222e', color: '#fff' }} className="modal-colored-body">
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}
        
        {success && (
          <div className="alert alert-success">{success}</div>
        )}
        
        <h5 style={{ color: '#fff' }}>Items for Sale</h5>
        {loading ? (
          <p style={{ color: '#fff' }}>Loading shop items...</p>
        ) : shopItems.length === 0 ? (
          <p style={{ color: '#fff' }}>This shop doesn't have any items for sale.</p>
        ) : (
          <div className="shop-items-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
            {shopItems.map(item => {
              const quantity = purchaseQuantities[item.id] || 1;
              const totalCost = item.price * quantity;
              const canAffordItem = canAfford(item, quantity);
              
              return (
                <div key={item.id} className="shop-item-card" style={{
                  border: '1px solid #2a3042',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: '#1d222e',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <img 
                      src={item.item_image} 
                      alt={item.item_name} 
                      style={{ 
                        width: '48px', 
                        height: '48px', 
                        marginRight: '10px',
                        objectFit: 'contain'
                      }} 
                    />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{item.item_name}</div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        color: '#28a745',
                        fontSize: '14px'
                      }}>
                        <img src="items/coin.png" alt="Coin" style={{ height: '14px', marginRight: '4px' }} /> 
                        {formatNumber(item.price)}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span>Available:</span>
                      <span>{formatNumber(item.quantity)}</span>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                      <label htmlFor={`quantity-${item.id}`} style={{ marginRight: '8px', fontSize: '13px' }}>Qty:</label>
                      <Input
                        id={`quantity-${item.id}`}
                        type="number"
                        value={quantity}
                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                        min="1"
                        max={item.quantity}
                        style={{ width: '60px', height: '32px', padding: '2px 8px' }}
                      />
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '6px',
                      backgroundColor: '#2a3042',
                      borderRadius: '4px',
                      marginBottom: '10px'
                    }}>
                      <span style={{ fontSize: '13px' }}>Total:</span>
                      <span style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        fontWeight: 'bold',
                        color: canAffordItem ? '#28a745' : '#dc3545'
                      }}>
                        <img src="items/coin.png" alt="Coin" style={{ height: '14px', marginRight: '4px' }} /> 
                        {formatNumber(totalCost)}
                      </span>
                    </div>
                    
                    <Button 
                      color={canAffordItem ? "success" : "secondary"}
                      block
                      size="sm"
                      onClick={() => handlePurchase(item)}
                      disabled={loading || !canAffordItem}
                    >
                      {canAffordItem ? 'Buy Now' : 'Cannot Afford'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ backgroundColor: '#1d222e', color: '#fff', borderTop: '1px solid #2a3042' }} className="modal-colored-footer">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PlayerShopBrowseModal;
